// Type imports
import type React from 'react'
import type { NextPage, GetStaticProps } from 'next'
import type { LinkTypes, I18nextKey, ActionTypes } from '../types'
import type { RequiredNs as ComponentNs } from '../components/localized/types'

// External imports
import Image from '../components/NextImage'
import { useRouter } from 'next/router'
import { NextSeo } from 'next-seo'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { useTranslation, Trans } from 'react-i18next'

import styled from '@emotion/styled'

// Others
import useScrollPassed from '../hooks/use-scrolled-passed'

import Header from '../components/localized/Header'
import Footer from '../components/localized/Footer'
import PlatformLogos from '../components/localized/PlatformLogos'
import Testimonials from '../components/localized/Testimonials'

import {
  FlexHeroSection,
  FlexItem as HeroFlexItem,
  Title as HeroTitle,
  Description as HeroDescription
} from '../components/Hero'
import { Section, FlexContainer, Item, FlexItem, Title, Description } from '../components/Section'
import { CenteredSection, Title as CenteredTitle } from '../components/CenteredSection'
import ColoredBackground from '../components/ColoredBackground'

import PrimaryAction from '../components/PrimaryAction'
import SecondaryAction from '../components/SecondaryAction'
import Highlight, { HighlightColor } from '../components/Highlight'
import FeatureGrid, { FeatureBlock } from '../components/FeatureGrid'

import BottomCta from '../components/BottomCta'

import SpellingFeatureImage from '../../public/assets/feature-images/fr/spelling.svg'

import { defaultPageSpecificSEO, pageSpecificCtaLink } from '../constants'

/**
 * Load all the namespaces required for this page include all localized components
 */
type RequiredNs = 'francais' | 'common' | ComponentNs
const namespaces: Record<RequiredNs, true> = { common: true, francais: true }
export const getStaticProps: GetStaticProps = async ({ locale }) => ({
  props: {
    ...(await serverSideTranslations(locale ?? '', Object.keys(namespaces)))
  }
})

/**
 * Constants & Styles
 */

const HERO_BACKGROUND = 'red'

const PaddedLinkButton = styled(PrimaryAction)`
  margin-top: 32px;
`

/**
 * Types for structure of page
 */

// did this manually for each key because it was a pain to sort out typescript issues
// but it would be better if we just type it so its just the prefix of the key needed
type FrancaisKey = I18nextKey<typeof import ('../../public/locales/en/francais.json')>
interface FlexSectionContent extends Record<'titleKey' | 'descriptionKey', FrancaisKey> {
  img: { src: any } & Record<'altKey', FrancaisKey>
  action?: LinkTypes & Record<'textKey', FrancaisKey>
  color?: HighlightColor
}

type FeatureBlockContent = (
  Record<'titleKey' | 'descriptionKey', FrancaisKey> &
  Omit<FeatureBlock, 'title' | 'description'> &
  Partial<LinkTypes> // IDK why but typescript does not find the 'linkTypes' in FeatureBlock?
)

/**
 * Mapping json keys for content structure
 */

const FEATURES: FlexSectionContent[] = [
  {
    color: 'green',
    titleKey: 'features.grammar.TITLE',
    descriptionKey: 'features.grammar.DESCRIPTION',
    img: { src: require('../../public/assets/feature-images/fr/grammar.svg'), altKey: 'features.grammar.FRENCH_GRAMMAR_IMG_ALT' }
  },
  {
    color: 'purple',
    titleKey: 'features.rewrite.TITLE',
    descriptionKey: 'features.rewrite.DESCRIPTION',
    img: { src: require('../../public/assets/feature-images/fr/rewrite.svg'), altKey: 'features.rewrite.FRENCH_STYLE_IMG_ALT' },
    action: {
      href: '/paraphrasing',
      textKey: 'features.rewrite.ACTION_TEXT'
    }
  }
]

const ADV_FEATURES: FeatureBlockContent[] = [
  {
    href: '/pro',
    titleKey: 'advfeatures.style.TITLE',
    descriptionKey: 'advfeatures.style.DESCRIPTION',
    icon: { src: require('../../public/assets/icons/improve.svg'), alt: '' },
    bgColor: 'yellow'
  },
  {
    titleKey: 'advfeatures.punctuation.TITLE',
    descriptionKey: 'advfeatures.punctuation.DESCRIPTION',
    icon: { src: require('../../public/assets/icons/quote.svg'), alt: '' },
    bgColor: 'purple'
  },
  {
    titleKey: 'advfeatures.thesaurus.TITLE',
    descriptionKey: 'advfeatures.thesaurus.DESCRIPTION',
    icon: { src: require('../../public/assets/icons/improve.svg'), alt: '' },
    bgColor: 'red'
  },
  {
    titleKey: 'advfeatures.multilingual.TITLE',
    descriptionKey: 'advfeatures.multilingual.DESCRIPTION',
    icon: { src: require('../../public/assets/icons/open-book.svg'), alt: '' },
    bgColor: 'purple'
  },
  {
    href: '/readability',
    titleKey: 'advfeatures.stats.TITLE',
    descriptionKey: 'advfeatures.stats.DESCRIPTION',
    icon: { src: require('../../public/assets/icons/chart-bar.svg'), alt: '' },
    bgColor: 'red'
  }
]

const Francais: NextPage = () => {
  const router = useRouter()

  const { t } = useTranslation('francais')
  const { t: tCommon } = useTranslation('common')

  const ctaAction: ActionTypes = { ...t('CTA_BUTTON'), ...pageSpecificCtaLink(router.pathname, router.locale) }

  // Controls for the header to only go sticky after user passes the fold
  const { ref: heroCtaRef, scrolledPassed } = useScrollPassed()

  return (
    <>
      <NextSeo
        {...defaultPageSpecificSEO(router.pathname, router.locale)}
        {...t('SEO')}
      />

      {/* Above the fold */}
      <Header
        t={tCommon}
        action={ctaAction}
        data-sticky={scrolledPassed}
        bgColor={HERO_BACKGROUND}
        dark
      />
      <ColoredBackground bgColor={HERO_BACKGROUND}>
        {/* Hero */}
        <FlexHeroSection>
          <HeroFlexItem>
            <HeroTitle>{t('hero.TITLE')}</HeroTitle>
            <HeroDescription>{t('hero.DESCRIPTION')}</HeroDescription>
            <PrimaryAction {...ctaAction} ref={heroCtaRef} clickId='hero-cta' />
          </HeroFlexItem>
          <HeroFlexItem>
            <Image src={SpellingFeatureImage} alt={t('hero.FRENCH_SPELLING_IMG_ALT')} priority />
          </HeroFlexItem>
        </FlexHeroSection>
      </ColoredBackground>

      {/* Features */}
      <Section bgColor='gray'>
        {FEATURES.map(({ titleKey, descriptionKey, img, action, color }, i) => {
          return (
          <FlexContainer key={titleKey} direction={i % 2 ? 'row-reverse' : 'row'}>
            <Item>
              <Image src={img.src} alt={t(img.altKey) as string} height={345} width={483} />
            </Item>
            <FlexItem>
              <div>
                <Title>
                  <Trans
                    t={t}
                    i18nKey={titleKey}
                    components={[<Highlight key={i} color={color} />]}
                  />
                </Title>
                <Description>{t(descriptionKey)}</Description>
              </div>
              {action && <SecondaryAction {...action} text={t(action.textKey) as string} />}
            </FlexItem>
          </FlexContainer>
          )
        })}
      </Section>

      {/* Advanced features */}
      <CenteredSection>
        <CenteredTitle>
          <Trans t={t} i18nKey={'advfeatures.TITLE'}><Highlight color='yellow'/></Trans>
        </CenteredTitle>
        <FeatureGrid
          list={ADV_FEATURES.map(({ titleKey, descriptionKey, ...rest }) => ({
            ...rest,
            title: t(titleKey) as string,
            description: t(descriptionKey) as string
          }))}
        />
      </CenteredSection>

      {/* Platforms */}
      <CenteredSection>
        <CenteredTitle>
          <Trans t={t} i18nKey='platforms.TITLE'><Highlight color='yellow'/></Trans>
        </CenteredTitle>
        <PlatformLogos t={tCommon} platforms={['google-docs', 'gmail', 'outlook', 'facebook', 'linkedin', 'wordpress']} />
        <PaddedLinkButton
          text={t('platforms.ACTION_TEXT')}
          {...pageSpecificCtaLink('platform', router.locale)}
          clickId='add-to-chrome'
        />
      </CenteredSection>

      {/* Testimonials */}
      <Testimonials t={tCommon} description={t('testimonials.DESCRIPTION')} />

      {/* BottomCta */}
      <BottomCta action={ctaAction} {...t('BOTTOM_CTA')} />

      {/* Footer */}
      <Footer t={tCommon} />
    </>
  )
}

export default Francais
